import { Trash2 } from "lucide-react";
import { Input } from "./ui/input";
import type { VariantForm } from "../types/product";
import { newVariant } from "../lib/variants";
import { cn } from "../lib/utils";

const PRESETS = [
	{ label: "250 g", grams: 250 },
	{ label: "500 g", grams: 500 },
	{ label: "1 kg", grams: 1000 },
];

const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-wide text-muted-foreground";

interface VariantsEditorProps {
	variants: VariantForm[];
	onChange: (variants: VariantForm[]) => void;
	error?: string | null;
}

/**
 * Formati di un miele: nome, peso, prezzo/sconto e se è in vendita. Le
 * quantità si inseriscono solo per i formati nuovi; quelli già salvati si
 * riforniscono dalla tabella del catalogo.
 */
export default function VariantsEditor({ variants, onChange, error }: VariantsEditorProps) {
	const update = (index: number, changes: Partial<VariantForm>) => {
		onChange(
			variants.map((v, i) => {
				if (i !== index) return v;
				const next = { ...v, ...changes };
				// Prezzo di vendita calcolato dallo sconto sul prezzo pieno
				if (("original_price" in changes || "discount_percent" in changes) && next.original_price && next.discount_percent) {
					next.price = Number((next.original_price * (1 - next.discount_percent / 100)).toFixed(2));
				}
				return next;
			}),
		);
	};

	const remove = (index: number) => onChange(variants.filter((_, i) => i !== index));

	const add = (label: string, grams: number) => {
		const sortOrder = Math.max(0, ...variants.map((v) => v.sort_order)) + 1;
		const all = [...variants, newVariant(label, grams, sortOrder)];
		// Ordine di listino: dal più piccolo al più grande
		onChange(all.sort((a, b) => a.grams - b.grams).map((v, i) => ({ ...v, sort_order: i + 1 })));
	};

	const missingPresets = PRESETS.filter((p) => !variants.some((v) => v.grams === p.grams));
	const num = (value: string) => (value === "" ? 0 : Number(value));

	return (
		<div className="space-y-3">
			{error && <p className="text-xs font-semibold text-danger">{error}</p>}

			{variants.length === 0 && (
				<p className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
					Nessun formato: aggiungine almeno uno.
				</p>
			)}

			{variants.map((v, i) => {
				const discounted = !!v.discount_percent && !!v.original_price;
				const isNew = !v.id;
				return (
					<div
						key={v.id ?? `new-${i}`}
						className={cn("rounded-lg border border-border p-3", !v.active && "bg-muted/60")}
					>
						<div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
							<div>
								<label htmlFor={`variant-label-${i}`} className={labelCls}>Formato *</label>
								<Input
									id={`variant-label-${i}`}
									value={v.label}
									placeholder="500 g"
									onChange={(e) => update(i, { label: e.target.value })}
								/>
							</div>
							<div>
								<label htmlFor={`variant-grams-${i}`} className={labelCls}>Grammi *</label>
								<Input
									id={`variant-grams-${i}`}
									type="number"
									min={1}
									step={1}
									value={v.grams || ""}
									onChange={(e) => update(i, { grams: num(e.target.value) })}
								/>
							</div>
							<div>
								<label htmlFor={`variant-original-${i}`} className={labelCls}>Prezzo pieno (€)</label>
								<Input
									id={`variant-original-${i}`}
									type="number"
									min={0}
									step={0.01}
									value={v.original_price ?? ""}
									placeholder="—"
									onChange={(e) => update(i, { original_price: e.target.value === "" ? null : num(e.target.value) })}
								/>
							</div>
							<div>
								<label htmlFor={`variant-discount-${i}`} className={labelCls}>Sconto (%)</label>
								<Input
									id={`variant-discount-${i}`}
									type="number"
									min={0}
									max={100}
									step={1}
									value={v.discount_percent ?? ""}
									placeholder="—"
									onChange={(e) => update(i, { discount_percent: e.target.value === "" ? null : num(e.target.value) })}
								/>
							</div>
							<div>
								<label htmlFor={`variant-price-${i}`} className={labelCls}>Prezzo vendita (€) *</label>
								<Input
									id={`variant-price-${i}`}
									type="number"
									min={0}
									step={0.01}
									value={v.price || ""}
									readOnly={discounted}
									className={cn(discounted && "bg-muted")}
									onChange={(e) => update(i, { price: num(e.target.value) })}
								/>
							</div>
						</div>

						<div className="mt-3 flex flex-wrap items-end justify-between gap-3">
							{isNew ? (
								<div className="flex gap-3">
									<div className="w-28">
										<label htmlFor={`variant-inventory-${i}`} className={labelCls}>In magazzino</label>
										<Input
											id={`variant-inventory-${i}`}
											type="number"
											min={0}
											value={v.inventory}
											onChange={(e) => update(i, { inventory: num(e.target.value) })}
										/>
									</div>
									<div className="w-28">
										<label htmlFor={`variant-stock-${i}`} className={labelCls}>Online</label>
										<Input
											id={`variant-stock-${i}`}
											type="number"
											min={0}
											max={v.inventory}
											value={v.stock}
											onChange={(e) => update(i, { stock: num(e.target.value) })}
										/>
									</div>
								</div>
							) : (
								<p className="text-xs text-muted-foreground">
									{v.inventory} in magazzino · {v.stock} online — si cambiano dal catalogo
								</p>
							)}
							<div className="flex items-center gap-3">
								<label className="flex items-center gap-2 text-sm text-foreground">
									<input
										type="checkbox"
										checked={v.active}
										onChange={(e) => update(i, { active: e.target.checked })}
										className="h-4 w-4 accent-primary"
									/>
									In vendita
								</label>
								<button
									type="button"
									onClick={() => remove(i)}
									className="flex h-10 w-10 md:h-8 md:w-8 items-center justify-center rounded-lg text-danger hover:bg-destructive/10"
									title={isNew ? "Togli formato" : "Elimina formato (per nasconderlo basta togliere 'In vendita')"}
									aria-label={`Elimina formato ${v.label}`}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</button>
							</div>
						</div>
					</div>
				);
			})}

			<div className="flex flex-wrap gap-2">
				{missingPresets.map((p) => (
					<button
						key={p.grams}
						type="button"
						onClick={() => add(p.label, p.grams)}
						className="h-10 md:h-8 rounded-lg bg-card px-3 text-xs font-bold text-foreground shadow-[inset_0_0_0_1px_hsl(var(--border))] hover:bg-muted"
					>
						+ {p.label}
					</button>
				))}
				<button
					type="button"
					onClick={() => add("", 0)}
					className="h-10 md:h-8 rounded-lg bg-card px-3 text-xs font-bold text-foreground shadow-[inset_0_0_0_1px_hsl(var(--border))] hover:bg-muted"
				>
					+ Altro formato
				</button>
			</div>
		</div>
	);
}
